import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { PlayArrow, Stop, EventNote, MyLocation, Login, SwapHoriz } from '@mui/icons-material';
import { formatTimeEntry } from '../utils/dateUtils';
import { timeService } from '../services/timeService';
import { kioskService } from '../services/kioskService';
import KioskAnnouncementBanner from '../components/kiosk/KioskAnnouncementBanner';
import { useAuth } from '../hooks/useAuth';
import {
  getCurrentPosition,
  getDeviceName,
  getKioskFallbackLocationLabel,
  getKioskLocationFallbackPayload,
  LOCATION_PERMISSION_DENIED,
  LOCATION_ENABLE_INSTRUCTIONS
} from '../hooks/usePunchLocation';

const formatTime = (v) => formatTimeEntry(v, 'MM/dd/yyyy HH:mm');

function kioskLocationExtras(position) {
  if (position) return {};
  return getKioskLocationFallbackPayload();
}

const DIALOG_TITLES = {
  clock_in: 'Clock In',
  switch_task: 'Switch Task',
  clock_out: 'Clock Out',
  timecard: 'View Timecard'
};

/**
 * Data Collection (Kiosk) — Clock In / Switch Task / Clock Out with PIN.
 * Clock In and Switch Task: PIN first, then pick a Shop Task.
 * When logged in: View Timecard uses PIN + session.
 * Employee Portal opens the standard login page.
 */
const TimeTrackingKiosk = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isLoggedIn = !!user;

  const goToEmployeePortal = async () => {
    if (isLoggedIn) {
      await logout();
    }
    navigate('/login');
  };
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinAction, setPinAction] = useState(null);
  const [pin, setPin] = useState('');
  const [punching, setPunching] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [timecardDialogOpen, setTimecardDialogOpen] = useState(false);
  const [timecardData, setTimecardData] = useState(null);
  const [timecardLoading, setTimecardLoading] = useState(false);
  const [requestingLocation, setRequestingLocation] = useState(false);
  // 'pin' → 'task' for clock_in / switch_task
  const [step, setStep] = useState('pin');
  const [taskContext, setTaskContext] = useState(null);
  const [kioskTasks, setKioskTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [selectedFunctionId, setSelectedFunctionId] = useState(null);
  const kioskFallbackLocationLabel = getKioskFallbackLocationLabel();
  const [announcements, setAnnouncements] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadAnnouncements = async () => {
      try {
        const data = await kioskService.getAnnouncements();
        if (!cancelled) setAnnouncements(data);
      } catch {
        if (!cancelled) setAnnouncements(null);
      }
    };
    loadAnnouncements();
    const interval = window.setInterval(loadAnnouncements, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const handleAllowLocation = async () => {
    setRequestingLocation(true);
    setError('');
    try {
      await getCurrentPosition();
      setError('');
    } catch (err) {
      const msg = err.message === LOCATION_PERMISSION_DENIED
        ? `${err.message} ${LOCATION_ENABLE_INSTRUCTIONS}`
        : (err.message || 'Location is required.');
      setError(msg);
    } finally {
      setRequestingLocation(false);
    }
  };

  const openPinDialog = (action) => {
    setPinAction(action);
    setPin('');
    setError('');
    setSuccessMessage('');
    setStep('pin');
    setTaskContext(null);
    setSelectedTaskId('');
    setSelectedFunctionId(null);
    setPinDialogOpen(true);
  };

  const closePinDialog = () => {
    setPinDialogOpen(false);
    setPinAction(null);
    setPin('');
    setError('');
    setSuccessMessage('');
    setStep('pin');
    setTaskContext(null);
    setSelectedTaskId('');
    setSelectedFunctionId(null);
  };

  const getPunchDevice = async (label) => {
    let position = null;
    try {
      position = await getCurrentPosition();
    } catch (locErr) {
      if (locErr.message === LOCATION_PERMISSION_DENIED) {
        setError(`${locErr.message} ${label} without location. ${LOCATION_ENABLE_INSTRUCTIONS}`);
      }
    }
    const device_name = getDeviceName();
    return { position, device_name };
  };

  /** Step 1 for clock-in / switch: identify the employee and load tasks. */
  const loadTaskStep = async (pinTrim) => {
    setPunching(true);
    setError('');
    try {
      const [ctxRes, tasksRes] = await Promise.all([
        timeService.kioskTaskContext(pinTrim),
        timeService.kioskTasks()
      ]);
      const ctx = { ...ctxRes.data, employeeName: ctxRes.employeeName };
      if (pinAction === 'clock_in' && ctx.clockedIn) {
        setError(`You are already clocked in on ${ctx.currentTaskLabel || 'a task'}. Use Switch Task or Clock Out.`);
        return;
      }
      if (pinAction === 'switch_task' && !ctx.clockedIn) {
        setError('You are not clocked in. Use Clock In first.');
        return;
      }
      const tasks = tasksRes.data || [];
      setKioskTasks(tasks);
      setTaskContext(ctx);
      if (pinAction === 'clock_in') {
        const suggested = tasks.find((t) => t.id === ctx.suggestedTaskId) || tasks.find((t) => t.is_default);
        setSelectedTaskId(suggested ? String(suggested.id) : '');
        setSelectedFunctionId(suggested && suggested.id === ctx.suggestedTaskId ? ctx.suggestedFunctionId ?? null : null);
      } else {
        setSelectedTaskId('');
        setSelectedFunctionId(null);
      }
      setStep('task');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to check PIN');
    } finally {
      setPunching(false);
    }
  };

  const taskFunctions = (taskId) =>
    kioskTasks.find((t) => String(t.id) === String(taskId))?.functions || [];

  const needsFunctionPick = () => taskFunctions(selectedTaskId).length > 1 && selectedFunctionId == null;

  const handleClockIn = async (pinTrim) => {
    if (!selectedTaskId) {
      setError('Pick a task.');
      return;
    }
    if (needsFunctionPick()) {
      setError('Pick the function you are doing.');
      return;
    }
    try {
      setPunching(true);
      setError('');
      const { position, device_name } = await getPunchDevice('Clocking in');
      if (!device_name || device_name === 'unknown') {
        setError('Device name is required. Set it in settings or use a named device.');
        return;
      }
      const res = await timeService.kioskPunch({
        pin: pinTrim,
        action: 'clock_in',
        shop_task_id: selectedTaskId,
        work_function_id: selectedFunctionId ?? undefined,
        latitude_in: position ? position.latitude : null,
        longitude_in: position ? position.longitude : null,
        device_name,
        device_info: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        ...kioskLocationExtras(position)
      });
      setError('');
      const taskLabel = res.data?.task_label ? ` on ${res.data.task_label}` : '';
      setSuccessMessage(`Clocked in as ${res.employeeName || 'OK'}${taskLabel}`);
      setTimeout(closePinDialog, 1800);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to clock in');
    } finally {
      setPunching(false);
    }
  };

  const handleSwitchTask = async (pinTrim) => {
    if (!selectedTaskId) {
      setError('Pick the task you are switching to.');
      return;
    }
    if (needsFunctionPick()) {
      setError('Pick the function you are switching to.');
      return;
    }
    try {
      setPunching(true);
      setError('');
      const res = await timeService.kioskPunch({
        pin: pinTrim,
        action: 'switch_task',
        shop_task_id: selectedTaskId,
        work_function_id: selectedFunctionId ?? undefined
      });
      setSuccessMessage(`${res.employeeName || 'OK'} is now on ${res.data?.task_label || 'the new task'}`);
      setTimeout(closePinDialog, 1800);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to switch task');
    } finally {
      setPunching(false);
    }
  };

  const handleClockOut = async (pinTrim) => {
    try {
      setPunching(true);
      setError('');
      const { position, device_name } = await getPunchDevice('Clocking out');
      if (!device_name || device_name === 'unknown') {
        setError('Device name is required.');
        return;
      }
      const res = await timeService.kioskPunch({
        pin: pinTrim,
        action: 'clock_out',
        latitude_out: position ? position.latitude : null,
        longitude_out: position ? position.longitude : null,
        device_name,
        device_info: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        ...kioskLocationExtras(position)
      });
      setError('');
      setSuccessMessage(`Clocked out. ${res.employeeName || 'Done'}`);
      setTimeout(closePinDialog, 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to clock out');
    } finally {
      setPunching(false);
    }
  };

  const handleTimecard = async (pinTrim) => {
    if (!isLoggedIn) {
      setError('Please log in to the app to view your timecard.');
      return;
    }
    try {
      setTimecardLoading(true);
      setError('');
      const res = await timeService.kioskTimecard(pinTrim);
      setTimecardData(res);
      closePinDialog();
      setTimecardDialogOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load timecard');
    } finally {
      setTimecardLoading(false);
    }
  };

  const handlePinSubmit = async () => {
    const pinTrim = String(pin).trim();
    if (!pinTrim) {
      setError('Enter your PIN');
      return;
    }
    if (pinAction === 'clock_in' || pinAction === 'switch_task') {
      if (step === 'pin') return loadTaskStep(pinTrim);
      return pinAction === 'clock_in' ? handleClockIn(pinTrim) : handleSwitchTask(pinTrim);
    }
    if (pinAction === 'clock_out') return handleClockOut(pinTrim);
    if (pinAction === 'timecard') return handleTimecard(pinTrim);
    return undefined;
  };

  const selectedTask = kioskTasks.find((t) => String(t.id) === String(selectedTaskId)) || null;
  const selectedFunctions = selectedTask?.functions || [];
  const isCurrentTask = (t) => pinAction === 'switch_task' && t?.id === taskContext?.currentTaskId;
  const switchOptions =
    pinAction === 'switch_task'
      ? kioskTasks.filter((t) => !isCurrentTask(t) || (t.functions || []).length > 1)
      : kioskTasks;

  const primaryLabel = (() => {
    if (pinAction === 'clock_in') return step === 'pin' ? 'Next' : 'Clock In';
    if (pinAction === 'switch_task') return step === 'pin' ? 'Next' : 'Switch';
    if (pinAction === 'clock_out') return 'Clock Out';
    return 'OK';
  })();
  const primaryColor = pinAction === 'clock_out' ? 'error' : pinAction === 'clock_in' ? 'success' : 'primary';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 3,
        px: 2,
        bgcolor: 'grey.50',
      }}
    >
      <KioskAnnouncementBanner config={announcements} />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Data Collection
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tap an option, then enter your PIN
      </Typography>

      <Button
        variant="outlined"
        size="small"
        startIcon={<MyLocation />}
        onClick={handleAllowLocation}
        disabled={requestingLocation}
        sx={{ mb: 1 }}
        title="Click first to trigger Chrome's location permission popup"
      >
        {requestingLocation ? 'Requesting…' : 'Allow Location (required for clock in/out)'}
      </Button>
      {kioskFallbackLocationLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, maxWidth: 360 }}>
          If GPS is unavailable, Clock-In/Out Location will show: <strong>{kioskFallbackLocationLabel}</strong>
          {' '}(GPS success still shows the normal site name, e.g. Main Office).
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 360, width: '100%' }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={<PlayArrow />}
          onClick={() => openPinDialog('clock_in')}
          sx={{ py: 2, fontSize: '1.1rem', fontWeight: 600 }}
        >
          Clock In
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<SwapHoriz />}
          onClick={() => openPinDialog('switch_task')}
          sx={{ py: 2, fontSize: '1.1rem', fontWeight: 600 }}
        >
          Switch Task
        </Button>
        <Button
          variant="contained"
          color="error"
          size="large"
          startIcon={<Stop />}
          onClick={() => openPinDialog('clock_out')}
          sx={{ py: 2, fontSize: '1.1rem', fontWeight: 600 }}
        >
          Clock Out
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<EventNote />}
          onClick={() => openPinDialog('timecard')}
          sx={{ py: 2, fontSize: '1rem' }}
        >
          View Timecard
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<Login />}
          onClick={goToEmployeePortal}
          sx={{ py: 2, fontSize: '1rem' }}
        >
          Employee Portal
        </Button>
      </Box>
      </Box>

      {/* PIN / task dialog */}
      <Dialog open={pinDialogOpen} onClose={closePinDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{DIALOG_TITLES[pinAction] || ''}</DialogTitle>
        <DialogContent>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 1 }}>{successMessage}</Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 1 }}
              onClose={() => setError('')}
              action={
                error.includes('Location') && (
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<MyLocation />}
                    onClick={handleAllowLocation}
                    disabled={requestingLocation}
                  >
                    {requestingLocation ? 'Requesting…' : 'Allow Location'}
                  </Button>
                )
              }
            >
              {error}
            </Alert>
          )}

          {step === 'pin' && (
            <TextField
              fullWidth label="PIN" type="password"
              inputProps={{ inputMode: 'numeric', maxLength: 10, autoComplete: 'off' }}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
              autoFocus disabled={punching || timecardLoading}
              sx={{ mt: 1 }}
            />
          )}

          {step === 'task' && taskContext && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Hi {taskContext.employeeName || 'there'} — what are you working on?
              </Typography>
              {pinAction === 'switch_task' && (
                <Typography variant="body2" color="text.secondary">
                  Currently on <strong>{taskContext.currentTaskLabel || '—'}</strong>
                  {taskContext.currentSince ? ` since ${formatTimeEntry(taskContext.currentSince, 'h:mm a')}` : ''}
                </Typography>
              )}
              {pinAction === 'clock_in' && taskContext.resumed && (
                <Typography variant="body2" color="text.secondary">
                  Picked up where you left off. Change it if you are starting something else.
                </Typography>
              )}
              <FormControl fullWidth disabled={punching}>
                <InputLabel>Task</InputLabel>
                <Select
                  label="Task"
                  value={selectedTaskId}
                  onChange={(e) => {
                    setSelectedTaskId(e.target.value);
                    setSelectedFunctionId(null);
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
                >
                  {switchOptions.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)} sx={{ py: 1.25, display: 'block' }}>
                      <Typography variant="body1" sx={{ fontWeight: t.is_default ? 600 : 500 }}>
                        {t.label}
                      </Typography>
                      {t.customer_name && (
                        <Typography variant="caption" color="text.secondary">{t.customer_name}</Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedFunctions.length > 1 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                    Which function?
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    color="primary"
                    orientation={selectedFunctions.length > 3 ? 'vertical' : 'horizontal'}
                    value={selectedFunctionId}
                    onChange={(_e, v) => v != null && setSelectedFunctionId(v)}
                    disabled={punching}
                  >
                    {selectedFunctions.map((f) => {
                      const isCurrent = isCurrentTask(selectedTask) && f.id === taskContext?.currentFunctionId;
                      return (
                        <ToggleButton key={f.id} value={f.id} disabled={isCurrent} sx={{ py: 1.5, fontWeight: 600 }}>
                          {f.label}{isCurrent ? ' (current)' : ''}
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>
                </Box>
              )}
              {selectedTask?.shop_notes && (
                <Alert severity="info" icon={false} sx={{ whiteSpace: 'pre-wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Shop notes</Typography>
                  {selectedTask.shop_notes}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {step === 'task' && !successMessage && (
            <Button onClick={() => { setStep('pin'); setError(''); }} disabled={punching} sx={{ mr: 'auto' }}>
              Back
            </Button>
          )}
          <Button onClick={closePinDialog} disabled={punching}>Cancel</Button>
          {pinAction && (
            <Button
              variant="contained"
              color={primaryColor}
              onClick={handlePinSubmit}
              disabled={punching || timecardLoading || !!successMessage}
            >
              {punching || timecardLoading ? <CircularProgress size={24} /> : primaryLabel}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Timecard modal */}
      <Dialog open={timecardDialogOpen} onClose={() => setTimecardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Timecard — {timecardData?.employeeName || 'Last 14 days'}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Clock In</TableCell>
                  <TableCell>Clock Out</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Tasks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(timecardData?.data || []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatTime(row.clock_in)}</TableCell>
                    <TableCell>{formatTime(row.clock_out)}</TableCell>
                    <TableCell>{row.hours_worked != null ? Number(row.hours_worked).toFixed(2) : '—'}</TableCell>
                    <TableCell>
                      {row.clock_out ? row.task_summary || '—' : `Now: ${row.current_task_label || '—'}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(!timecardData?.data || timecardData.data.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No time entries in the last 14 days.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimecardDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTrackingKiosk;
